import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

interface HeaderProps {
  onOpenWaitlist?: (tab?: 'customer' | 'vendor' | 'rider') => void;
}

const Header: React.FC<HeaderProps> = ({ onOpenWaitlist }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleNavClick = (to: string) => () => {
    if (location.pathname === to) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    isActive
      ? 'inline-flex items-center justify-center rounded-full bg-orange-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-600'
      : 'inline-flex items-center justify-center rounded-full px-5 py-2 text-sm font-medium text-white transition-colors hover:opacity-80';

  return (
    <header className="absolute inset-x-0 top-0 z-30">
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-6 lg:px-8 py-4">
        <Link to="/" onClick={handleNavClick('/')} className="flex items-center">
          <img src="/images/Newlogo.svg" alt="MarketMate Logo" className="h-16 sm:h-20 w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-4">
          <div className="flex items-center gap-3 rounded-full px-3 py-2 shadow-2xl" style={{ backgroundColor: '#2E7D32', boxShadow: '0 25px 50px -12px rgba(46, 125, 50, 0.2)' }}>
            <NavLink to="/" onClick={handleNavClick('/')} className={navItemClass}>
              Home
            </NavLink>
            <NavLink to="/about" onClick={handleNavClick('/about')} className={navItemClass}>
              About Us
            </NavLink>
            <NavLink to="/contact" onClick={handleNavClick('/contact')} className={navItemClass}>
              Contact Us
            </NavLink>
            <NavLink to="/faq" onClick={handleNavClick('/faq')} className={navItemClass}>
              FAQ
            </NavLink>
            <button
              type="button"
              onClick={() => onOpenWaitlist && onOpenWaitlist('customer')}
              className="inline-flex items-center justify-center rounded-full px-5 py-2 text-sm font-medium text-white transition-colors hover:opacity-80 cursor-pointer"
            >
              Join Waitlist
            </button>
          </div>

          <button
            type="button"
            onClick={() => onOpenWaitlist && onOpenWaitlist('customer')}
            className="inline-flex items-center gap-2 rounded-full px-6 py-2 text-sm font-semibold text-white transition-colors hover:opacity-80 cursor-pointer"
            style={{ backgroundColor: '#2E7D32' }}
          >
            Get Early Access <span>→</span>
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full text-white transition-colors hover:opacity-80"
          style={{ backgroundColor: '#2E7D32' }}
        >
          <svg
            className={`w-6 h-6 transition-transform ${mobileMenuOpen ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden backdrop-blur-sm px-4 sm:px-6 py-6 space-y-3" style={{ backgroundColor: 'rgba(46, 125, 50, 0.95)' }}>
          <NavLink
            to="/"
            className={({ isActive }) =>
              `block w-full rounded-full px-5 py-3 text-center text-sm font-medium transition-colors text-white ${isActive
                ? 'bg-orange-500 hover:bg-orange-600'
                : 'hover:opacity-80'
              }`
            }
            style={({ isActive }) => !isActive ? { backgroundColor: '#2E7D32' } : {}}
            onClick={() => {
              setMobileMenuOpen(false);
              if (location.pathname === '/') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            Home
          </NavLink>
          <NavLink
            to="/about"
            className={({ isActive }) =>
              `block w-full rounded-full px-5 py-3 text-center text-sm font-medium transition-colors text-white ${isActive
                ? 'bg-orange-500 hover:bg-orange-600'
                : 'hover:opacity-80'
              }`
            }
            style={({ isActive }) => !isActive ? { backgroundColor: '#2E7D32' } : {}}
            onClick={() => {
              setMobileMenuOpen(false);
              if (location.pathname === '/about') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            About Us
          </NavLink>
          <NavLink
            to="/contact"
            className={({ isActive }) =>
              `block w-full rounded-full px-5 py-3 text-center text-sm font-medium transition-colors text-white ${isActive
                ? 'bg-orange-500 hover:bg-orange-600'
                : 'hover:opacity-80'
              }`
            }
            style={({ isActive }) => !isActive ? { backgroundColor: '#2E7D32' } : {}}
            onClick={() => {
              setMobileMenuOpen(false);
              if (location.pathname === '/contact') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            Contact Us
          </NavLink>
          <NavLink
            to="/faq"
            className={({ isActive }) =>
              `block w-full rounded-full px-5 py-3 text-center text-sm font-medium transition-colors text-white ${isActive
                ? 'bg-orange-500 hover:bg-orange-600'
                : 'hover:opacity-80'
              }`
            }
            style={({ isActive }) => !isActive ? { backgroundColor: '#2E7D32' } : {}}
            onClick={() => {
              setMobileMenuOpen(false);
              if (location.pathname === '/faq') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            FAQ
          </NavLink>
          <button
            type="button"
            className="block w-full rounded-full px-5 py-3 text-center text-sm font-medium transition-colors text-white hover:opacity-80 cursor-pointer"
            style={{ backgroundColor: '#2E7D32' }}
            onClick={() => {
              setMobileMenuOpen(false);
              onOpenWaitlist && onOpenWaitlist('customer');
            }}
          >
            Join Waitlist
          </button>
          <button
            type="button"
            className="block w-full rounded-full px-6 py-3 text-center text-sm font-semibold text-white hover:opacity-80 transition-colors cursor-pointer"
            style={{ backgroundColor: '#059669' }}
            onClick={() => {
              setMobileMenuOpen(false);
              onOpenWaitlist && onOpenWaitlist('vendor');
            }}
          >
            Become a Merchant Partner →
          </button>
        </div>
      )}
    </header>
  );
};

export default Header;
